import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router";

import { MakeChartPattern } from "../../../utils/MakeChartPattern";

import {
  GET_ALL_GAMES_RESULT,
  GET_ALL_JODI_CHART,
  GET_JACKPOT_JODI_CHART,
  GET_ALL_STARLINE_GAME_PANA_CHART,
} from "../../service/admin.service";

import {
  STARLINE_CHART_COMPONENTS,
  JACKPOT_CHART_COMPONENTS,
} from "../../../components/Config/chartComponent.config";

import { JODI_CHART_COMPONENTS } from "../../../components/Config/jodiChartComponent.config";
import { PANA_CHART_COMPONENTS } from "../../../components/Config/panaChartComponent.config";

import { ArrowDown, ArrowUp } from "lucide-react";
import Footer from "../Footer/Footer";

/* ================= NORMALIZE ================= */
const normalizeKey = (val) =>
  val?.replace(/\s+/g, "").replace(/-/g, "").toUpperCase();

/* ================= TIME NORMALIZE ================= */
function normalizeTime(timeStr) {
  if (!timeStr) return null;
  let t = timeStr.replace(/\s+/g, "").toUpperCase();
  const match = t.match(/^(\d{1,2}):(\d{2})(AM|PM)$/);
  if (!match) return null;

  let hour = parseInt(match[1], 10);
  let minute = match[2];
  let period = match[3];

  return `${hour}:${minute}${period}`;
}

const Charts = () => {
  const { type, name, id } = useParams();

  const topRef = useRef(null);
  const bottomRef = useRef(null);

  const iframeRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [DynamicGetCharts, setDynamicGetChart] = useState([]);

  const [iframeHeight, setIframeHeight] = useState("0px");
  const [isIframeLoading, setIsIframeLoading] = useState(true);

const formatName = (name) => {
  const nameMap = {
    LAXMIMORNING: "LAXMI MORNING",
    LAKSHMORNING: "LAKSH MORNING",
    SANDHYAMORNING: "SANDHYA MORNING",
    SRIDEVI: "SRI DEVI",
    TIMEBAZAR: "TIME BAZAR",
    MADHURDAY: "MADHUR DAY",
    LAXMIDAY: "LAXMI DAY",
    RAJDHANIDAY: "RAJDHANI DAY",
    MILANDAY: "MILAN DAY",
    SUPREMEDAY: "SUPREME DAY",
    LAKSHDAY: "LAKSH DAY",
    SANDHYADAY: "SANDHYA DAY",
    KALYAN: "KALYAN",

    SRIDEVINIGHT: "SRI DEVI NIGHT",
    LAXMINIGHT: "LAXMI NIGHT",
    LAKSHNIGHT: "LAKSH NIGHT",
    MADHURNIGHT: "MADHUR NIGHT",
    SUPREMENIGHT: "SUPREME NIGHT",
    MILANNIGHT: "MILAN NIGHT",
    SANDHYANIGHT: "SANDHYA NIGHT",
    KALYANNIGHT: "KALYAN NIGHT",
    RAJDHANINIGHT: "RAJDHANI NIGHT",
    MAINBAZAR: "MAIN BAZAR",
  };

  return nameMap[name] || name;
};



  const provider = name;
  const decodedTime = provider?.replace(/\s+/g, "");
  const decodedName = name?.replace(/-/g, " ");
  const chartTitle = {
    jodi: "JODI CHART",
    pana: "PANEL CHART",
    jackpot: "JACKPOT CHART",
    starline: "STARLINE CHART",
  };
  const reqData = { type, name, id };

  /* ================= API ================= */
  const getCharts = async () => {
    try {
      setLoading(true);
      let response;

      if (type === "pana") {
        response = await MakeChartPattern(GET_ALL_GAMES_RESULT, reqData);
      }

      if (type === "jodi") {
        response = await MakeChartPattern(GET_ALL_JODI_CHART, reqData);
      }

      if (type === "jackpot") {
        response = await MakeChartPattern(GET_JACKPOT_JODI_CHART, reqData);
      }

      if (type === "starline") {
        reqData.name = normalizeTime(decodedTime);
        response = await MakeChartPattern(
          GET_ALL_STARLINE_GAME_PANA_CHART,
          reqData,
        );
      }

      setDynamicGetChart(response?.data || []);
    } catch (err) {
      console.error("Chart API error", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getCharts();
  }, [type, provider]);

  /* ================= AUTO SCROLL ================= */
  useEffect(() => {
    if (!loading && DynamicGetCharts.length > 0) {
      const timer = setTimeout(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [loading, DynamicGetCharts]);

  /* ================= COMPONENT SELECT ================= */
  let ChartComponent = null;

  if (type === "starline") {
    ChartComponent = STARLINE_CHART_COMPONENTS[normalizeKey(name)];
  }

  if (type === "jackpot") {
    ChartComponent = JACKPOT_CHART_COMPONENTS[normalizeKey(name)];
  }

  if (type === "jodi") {
    ChartComponent = JODI_CHART_COMPONENTS[normalizeKey(name)];
  }

  if (type === "pana") {
    ChartComponent = PANA_CHART_COMPONENTS[normalizeKey(name)];
  }

  /* ================= IFRAME URL ================= */
  const chartUrl = `${"https://resultapi.org/"}${type}-chart/${normalizeKey(
    name,
  )}?embed=true&color=%231E6EBE`;

  /* ================= IFRAME HEIGHT LISTENER ================= */
  useEffect(() => {
    if (ChartComponent) return;

    const handleMessage = (event) => {
      if (event.data?.type === "SET_HEIGHT") {
        const newHeight = `${event.data.height}px`;
        setIframeHeight(newHeight);

        if (iframeRef.current) {
          iframeRef.current.style.height = newHeight;
        }
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [ChartComponent]);

  /* ================= SCROLL ================= */
  const scrollToTop = () =>
    topRef.current?.scrollIntoView({ behavior: "smooth" });

  const scrollToBottom = () =>
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="tw-relative tw-p-0 tw-pt-[30px] md:tw-px-[150px]">
      <div ref={topRef} />

      <h3 className="tw-text-center tw-font-bold tw-text-[#1e6ebe] tw-text-3xl md:tw-text-5xl tw-mb-4 uppercase">
        {formatName(name)} {chartTitle[type] || `${type} Chart`}
      </h3>

      {/* Scroll Bottom */}
      <div className="tw-flex tw-justify-center tw-mb-1">
        <button
          onClick={scrollToBottom}
          className="tw-bg-[#1e6ebe] tw-cursor-pointer tw-flex tw-items-center tw-text-white tw-px-4 tw-py-2 tw-rounded-2xl tw-shadow tw-font-bold tw-gap-2 hover:tw-scale-105 tw-transition-all"
        >
          <ArrowDown size={19} strokeWidth={2.75} />
          <span>Scroll To Bottom</span>
        </button>
      </div>

      {/* ================= MAIN RENDER ================= */}
      {ChartComponent ? (
        <ChartComponent chartData={DynamicGetCharts} />
      ) : (
        <div
          style={{
            minHeight: iframeHeight === "0px" ? "150px" : iframeHeight,
            position: "relative",
          }}
          className="tw-mb-2"
        >
          {/* Loader */}
          {isIframeLoading && (
            <div className="tw-absolute tw-inset-0 tw-flex tw-justify-center tw-items-center tw-bg-white tw-z-10">
              <div className="tw-space-y-3 tw-w-full tw-max-w-md tw-px-4">
                <div className="tw-h-4 tw-w-full tw-bg-gray-200 tw-animate-pulse tw-rounded"></div>
                <div className="tw-h-4 tw-w-full tw-bg-gray-200 tw-animate-pulse tw-rounded"></div>
                <div className="tw-h-4 tw-w-full tw-bg-gray-200 tw-animate-pulse tw-rounded"></div>
              </div>
            </div>
          )}

          {/* IFRAME */}
          <iframe
            ref={iframeRef}
            src={chartUrl}
            title="Charts"
            onLoad={() => setIsIframeLoading(false)}
            className="tw-w-full tw-border-0"
            scrolling="no"
            style={{
              width: "100%",
              height: iframeHeight,
              opacity: isIframeLoading ? 0 : 1,
            }}
          />
        </div>
      )}

      <div ref={bottomRef} />

      <Footer variant="mini" />
    </div>
  );
};

export default Charts;
